import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, renderList as _renderList } from "vue";
const _hoisted_1 = { class: "font-semibold text-h6" };
const _hoisted_2 = ["href"];
const _hoisted_3 = ["href"];
const _hoisted_4 = { key: 1 };
const _hoisted_5 = { class: "d-flex align-center ga-3" };
const _hoisted_6 = { class: "text-body-1" };
const _hoisted_7 = { class: "text-body-1 font-weight-semibold mb-2" };
const _hoisted_8 = { class: "text-body-2 mb-2" };
const _hoisted_9 = { class: "d-flex align-center justify-space-between" };
const _hoisted_10 = { class: "d-flex align-center ga-4 mb-4 text-body-2" };
const _hoisted_11 = { class: "d-flex align-center ga-2" };
const _hoisted_12 = { class: "d-flex align-center ga-2" };
const _hoisted_13 = { class: "font-medium" };
const _hoisted_14 = { class: "space-y-2" };
const _hoisted_15 = { class: "ml-4" };
const _hoisted_16 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_17 = { class: "space-y-2" };
const _hoisted_18 = { class: "mt-2" };
const _hoisted_19 = { class: "d-flex align-center justify-space-between" };
const _hoisted_20 = {
    class: "text-body-2",
    style: { "white-space": "pre-line" }
};
const _hoisted_21 = { class: "text-body-2 text-medium-emphasis" };
const _hoisted_22 = { key: 0 };
const _hoisted_23 = { key: 1 };
import { ref, computed, onMounted, onUnmounted, watch, provide, nextTick } from "vue";
import { intakeService } from "@/services/intakeService";
import CriteriaItem from "./PolicyReviewCriteriaItem.vue";
import { decamelizeKeys } from "humps";
const MAX_POLL_ATTEMPTS = 30; // 30 attempts * 10 seconds = 5 minutes max (safety margin)
const POLL_INTERVAL_MS = 10000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicyReviewCriteriaCard',
    props: {
        order: {},
        currentPageId: {},
        pageManager: {}
    },
    setup(__props) {
        const props = __props;
        // Initialize with a safe default structure to prevent undefined status errors
        const policyReview = ref({
            status: "processing",
            message: "Policy review is running",
        });
        const loading = ref(false);
        const criteriaTree = ref([]);
        const renderKey = ref(0);
        const criteriaState = ref({}); // Store user-modified statuses
        const pageAttachments = ref({}); // Store page attachments
        const retryingPolicyReview = ref(false);
        const switchingCandidate = ref(false);
        const showSwitchConfirmDialog = ref(false);
        const pendingCandidateIndex = ref(null);
        const errorSnackbar = ref(false);
        const errorSnackbarMessage = ref("");
        // Helper to get page attachments for a criterion id (handles undefined)
        const getAttachedPages = (id) => {
            if (!id)
                return [];
            return pageAttachments.value[id] || [];
        };
        // Polling timeout and retry management
        // Policy review typically takes 3~4 minutes, so we allow up to 5 minutes
        let pollInterval = null;
        let pollAttempts = 0;
        const isProcessing = computed(() => {
            // Don't show processing if we have data with criteria
            if (criteriaTree.value.length > 0) {
                return false;
            }
            // Safely check status with optional chaining
            const status = policyReview.value?.status;
            return status === "processing" || loading.value;
        });
        const isBackendProcessing = computed(() => policyReview.value?.status === "processing");
        const candidates = computed(() => policyReview.value?.candidates || []);
        const selectedCandidateIndex = computed(() => policyReview.value?.selectedCandidateIndex ?? 0);
        const hasCandidateSelector = computed(() => candidates.value.length > 1 && !isBackendProcessing.value && !switchingCandidate.value);
        const candidateItems = computed(() => candidates.value.map((c) => ({
            value: c.index,
            title: [c.payor, c.title].filter(Boolean).join(" — "),
        })));
        const policyInputsChanged = computed(() => {
            const inputs = policyReview.value?.inputs;
            if (!inputs || !props.order?.orderForm)
                return false;
            const norm = (val) => String(val ?? "")
                .trim()
                .toLowerCase();
            const orderForm = props.order?.orderForm;
            const insuranceCard = props.order?.insuranceCard;
            const fields = [
                [inputs.drug, orderForm?.therapyAdministration?.[0]?.name ?? ""],
                [inputs.diagnosis, orderForm?.diagnosisIcd10Code ?? ""],
                [inputs.payorName, orderForm?.extraData?.reimbursementPayorName || insuranceCard?.payorName || ""],
            ];
            return fields.some(([stored, current]) => {
                if (stored == null)
                    return false;
                return norm(stored) !== norm(current);
            });
        });
        const hasError = computed(() => {
            const status = policyReview.value?.status;
            const error = policyReview.value?.error;
            return (status === "failed" ||
                status === "no_policy_found" ||
                status === "missing_input" ||
                (status === "completed" && !!error));
        });
        const errorAlertType = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found" || status === "missing_input")
                return "warning";
            return "error";
        });
        const errorTitle = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found")
                return "No Policy Found";
            if (status === "missing_input")
                return "Missing Input";
            return "Policy Review Failed";
        });
        const errorMessage = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found")
                return "Unable to find drug payor policy.";
            if (status === "missing_input")
                return policyReview.value?.error || "Missing required input fields to run policy review.";
            return policyReview.value?.error || policyReview.value?.message || "An unknown error occurred during policy review.";
        });
        const shouldShow = computed(() => !!policyReview.value || isProcessing.value);
        const overallStatus = computed(() => {
            const hasFailures = criteriaTree.value.some((c) => {
                const status = getCriterionStatus(c);
                return status === "fail";
            });
            return hasFailures ? "Needs Review" : "Approved";
        });
        // Determine criteria title based on referral status
        const criteriaTitle = computed(() => {
            const referralStatus = policyReview.value?.referralStatus ||
                policyReview.value?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            if (referralStatus === "continuation") {
                return "Continuation of Therapy Criteria";
            }
            return "Initial Approval Criteria";
        });
        const policyDocumentName = computed(() => policyReview.value?.policyDocumentName || "");
        const policyDocumentUrl = computed(() => {
            const url = policyReview.value?.policyDocumentUrl || "";
            return url.startsWith("https://") || url.startsWith("http://") ? url : "";
        });
        const policyPayerName = computed(() => policyReview.value?.policyPayorName || policyReview.value?.policyPayerName || "");
        const policySourceLabel = computed(() => {
            const name = policyDocumentName.value;
            const payer = policyPayerName.value;
            if (!name && !payer)
                return "";
            if (payer && name)
                return `${payer} — ${name}`;
            return name || payer;
        });
        // Fetch policy review data
        const fetchPolicyReview = async () => {
            loading.value = true;
            try {
                const response = await intakeService.getPolicyReview(props.order.id);
                // Check if response has structured output even if status is processing
                const responseHasData = !!response?.structuredOutput;
                const responseStatus = response?.status;
                // Always update policyReview with the response if it exists
                // This ensures we show errors and completed states
                // CRITICAL: Always update with fresh API data if it has structured output
                // This ensures new data is displayed immediately without requiring a page refresh
                let shouldReparse = false; // Track if we need to re-parse criteria
                // Always update if response has structured output (new data available)
                if (responseHasData) {
                    // Check if this is actually new data by comparing with current data
                    const currentStructuredOutput = policyReview.value?.structuredOutput;
                    const newStructuredOutput = response?.structuredOutput;
                    const pageAttachmentsData = decamelizeKeys(response?.pageAttachments) || {};
                    if (pageAttachmentsData && Object.keys(pageAttachmentsData).length > 0) {
                        pageAttachments.value = { ...pageAttachments.value, ...pageAttachmentsData };
                    }
                    // Compare the data to see if it's different (simple JSON comparison)
                    const isNewData = !currentStructuredOutput || JSON.stringify(currentStructuredOutput) !== JSON.stringify(newStructuredOutput);
                    if (isNewData) {
                        shouldReparse = true; // Mark that we need to re-parse
                        // Clear existing criteria tree to force re-render
                        criteriaTree.value = [];
                    }
                    loading.value = false;
                }
                else if (response.error ||
                    responseStatus === "completed" ||
                    responseStatus === "failed" ||
                    responseStatus === "no_policy_found" ||
                    responseStatus === "missing_input") {
                    loading.value = false;
                }
                policyReview.value = response;
                const structuredOutput = responseHasData ? response?.structuredOutput : policyReview.value?.structuredOutput;
                // Always parse if we have new data from API, or if we don't have criteria yet
                if (shouldReparse || criteriaTree.value.length === 0) {
                    if (structuredOutput?.criteria && Array.isArray(structuredOutput?.criteria)) {
                        criteriaTree.value = [...structuredOutput.criteria];
                        await setCriteriaTree(response, responseHasData);
                        policyReview.value = {
                            ...policyReview.value,
                            structuredOutput: structuredOutput,
                        };
                    }
                }
            }
            catch (error) {
                // Ensure policyReview.value always has a valid structure
                const err = error;
                const errMessage = err.response?.data?.detail || err.message || "Failed to fetch policy review";
                policyReview.value = {
                    status: "failed",
                    error: errMessage,
                    message: errMessage,
                };
            }
            finally {
                loading.value = false;
            }
        };
        const setCriteriaTree = async (response, responseHasData) => {
            // Filter parsed criteria based on referral status
            const referralStatus = (responseHasData ? response : policyReview.value)?.referralStatus ||
                (responseHasData ? response : policyReview.value)?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            const filtered = filterCriteriaByReferralStatus(criteriaTree.value, referralStatus);
            // Create new array reference to trigger reactivity
            criteriaTree.value = [...filtered];
            if (criteriaTree.value.length > 0) {
                loading.value = false; // Ensure loading is stopped when we have data
                // Ensure status is set to completed so isProcessing returns false
                // Create a new object to ensure Vue reactivity detects the change
                policyReview.value = {
                    ...policyReview.value,
                    status: "completed",
                };
                // Stop polling immediately when we have data
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                    pollAttempts = 0;
                }
                // Force immediate reactive update - trigger a re-render
                // This ensures computed properties like isProcessing and shouldShow update
                await nextTick();
                renderKey.value += 1;
            }
        };
        // Filter criteria tree to show only sections relevant to referral status
        const filterCriteriaByReferralStatus = (criteria, referralStatus) => {
            if (!Array.isArray(criteria) || criteria.length === 0) {
                return criteria;
            }
            const normalizedStatus = String(referralStatus).toLowerCase().trim();
            // Define which section names are relevant for each status
            // Note: The actual section names from YAML are "Initial Approval Criteria" and "Continued Therapy Criteria"
            // "Medically Necessary Diagnoses" should always be shown regardless of referral status
            const relevantSections = {
                initial: ["Initial Approval Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
                continuation: ["Continued Therapy Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
            };
            const allowedSections = relevantSections[normalizedStatus] || relevantSections.initial;
            // Filter criteria recursively - sections are top-level items with type "section"
            const filterRecursive = (items, parentSectionName = null) => {
                return items
                    .map((item) => {
                    // Check if this is a section that should be included
                    const sectionName = item.text || item.name || "";
                    const isRelevantSection = allowedSections.some((allowed) => {
                        // Use exact match or case-insensitive contains for section names
                        const sectionLower = sectionName.toLowerCase();
                        const allowedLower = allowed.toLowerCase();
                        return sectionLower === allowedLower || sectionLower.includes(allowedLower);
                    });
                    // Determine the current section name (either this item if it's a section, or inherit from parent)
                    const currentSectionName = item.type === "section" ? sectionName : parentSectionName;
                    // If this item has children, filter them recursively
                    if (item.children && Array.isArray(item.children)) {
                        const filteredChildren = filterRecursive(item.children, currentSectionName);
                        // For sections (top-level items), only include if it's a relevant section
                        if (item.type === "section") {
                            // Top-level section - only include if it matches
                            if (isRelevantSection) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                        else {
                            // Nested group or criterion - include if parent section is relevant
                            // Check if parent section name matches allowed sections
                            const parentIsRelevant = currentSectionName
                                ? allowedSections.some((allowed) => {
                                    const parentLower = currentSectionName.toLowerCase();
                                    const allowedLower = allowed.toLowerCase();
                                    return parentLower === allowedLower || parentLower.includes(allowedLower);
                                })
                                : false;
                            if (parentIsRelevant) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                    }
                    // For leaf nodes (no children), include if parent section is relevant
                    const parentIsRelevant = currentSectionName
                        ? allowedSections.some((allowed) => {
                            const parentLower = currentSectionName.toLowerCase();
                            const allowedLower = allowed.toLowerCase();
                            return parentLower === allowedLower || parentLower.includes(allowedLower);
                        })
                        : false;
                    return parentIsRelevant ? item : null;
                })
                    .filter((item) => item !== null);
            };
            return filterRecursive(criteria);
        };
        const getCriterionStatus = (criterion) => {
            // Check if user has overridden the status
            if (criterion.id && criteriaState.value[criterion.id]) {
                return criteriaState.value[criterion.id];
            }
            // For parent nodes, calculate status from children
            if (criterion.children && criterion.children.length > 0) {
                const childStatuses = criterion.children.map((child) => getCriterionStatus(child));
                if (criterion.logic === "ALL") {
                    return childStatuses.every((s) => s === "pass") ? "pass" : "fail";
                }
                else if (criterion.logic === "ONE") {
                    return childStatuses.some((s) => s === "pass") ? "pass" : "fail";
                }
            }
            return criterion.status || "pending";
        };
        const onStatusChange = async (criterionId, newStatus) => {
            if (!criterionId)
                return;
            // Update local state immediately for responsive UI
            criteriaState.value[criterionId] = newStatus;
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    criteria_state: {
                        [criterionId]: newStatus,
                    },
                });
            }
            catch (error) {
                // Error updating criteria status
                const err = error;
                // Revert on error
                delete criteriaState.value[criterionId];
                // Show user-friendly error message
                const msg = err.response?.data?.errorDetails || err.message || "Failed to update status";
                errorSnackbarMessage.value = msg;
                errorSnackbar.value = true;
            }
        };
        const onCandidateChange = (index) => {
            if (index === selectedCandidateIndex.value)
                return;
            pendingCandidateIndex.value = index;
            showSwitchConfirmDialog.value = true;
        };
        const cancelCandidateSwitch = () => {
            showSwitchConfirmDialog.value = false;
            pendingCandidateIndex.value = null;
        };
        const confirmCandidateSwitch = async () => {
            if (pendingCandidateIndex.value === null)
                return;
            showSwitchConfirmDialog.value = false;
            switchingCandidate.value = true;
            if (policyReview.value) {
                policyReview.value = { ...policyReview.value, status: "processing", error: undefined };
            }
            try {
                const response = await intakeService.triggerPolicyReview(props.order.id, {
                    candidateIndex: pendingCandidateIndex.value,
                });
                if (response.status === "completed" && response.structuredOutput) {
                    policyReview.value = response;
                    const criteria = response.structuredOutput?.criteria;
                    criteriaTree.value = Array.isArray(criteria) ? [...criteria] : [];
                    await setCriteriaTree(response, true);
                }
                else {
                    policyReview.value = { ...policyReview.value, ...response };
                    criteriaTree.value = [];
                }
            }
            catch (error) {
                const err = error;
                policyReview.value = {
                    ...policyReview.value,
                    status: "failed",
                    error: err.response?.data?.error || err.response?.data?.detail || err.message || "Failed to switch candidate",
                };
            }
            finally {
                switchingCandidate.value = false;
                pendingCandidateIndex.value = null;
            }
        };
        const retryPolicyReview = async () => {
            if (retryingPolicyReview.value)
                return;
            retryingPolicyReview.value = true;
            loading.value = true;
            criteriaTree.value = [];
            if (policyReview.value) {
                policyReview.value = { ...policyReview.value, status: "processing", running: true, policyDocumentUrl: null };
            }
            try {
                const response = await intakeService.triggerPolicyReview(props.order.id);
                // Ensure response exists before using it
                if (!response) {
                    throw new Error("No response received from policy review trigger");
                }
                policyReview.value = response;
            }
            catch (error) {
                // Extract detailed error message from response
                const err = error;
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to trigger policy review";
                policyReview.value = {
                    status: "failed",
                    error: errorMessage,
                    message: errorMessage,
                };
            }
            finally {
                retryingPolicyReview.value = false;
                loading.value = false;
            }
        };
        const onPageAction = async (criterionId, action, pageId) => {
            if (!criterionId)
                return;
            const numericPageId = typeof pageId === "string" ? parseInt(pageId, 10) : pageId;
            if (action === "add" && numericPageId) {
                if (!pageAttachments.value[criterionId]) {
                    pageAttachments.value[criterionId] = [];
                }
                if (!pageAttachments.value[criterionId].includes(numericPageId)) {
                    pageAttachments.value[criterionId].push(numericPageId);
                }
            }
            else if (action === "remove" && numericPageId) {
                if (pageAttachments.value[criterionId]) {
                    pageAttachments.value[criterionId] = pageAttachments.value[criterionId].filter((id) => id !== numericPageId);
                }
            }
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    page_attachments: {
                        [criterionId]: pageAttachments.value[criterionId] || [],
                    },
                });
            }
            catch (error) {
                // Error updating page attachments
                const err = error;
                // Show user-friendly error message
                const msg = err.response?.data?.errorDetails || err.message || "Failed to update page";
                errorSnackbarMessage.value = msg;
                errorSnackbar.value = true;
            }
        };
        // Poll for updates if processing (but don't poll if we have completed data)
        // Watch both isProcessing and policyReview status to ensure we poll when needed
        watch([() => isProcessing.value, () => policyReview.value?.status], ([processing, status]) => {
            // Check if we have truly completed data (status is completed AND we have structured output)
            const hasCompletedData = status === "completed" && policyReview.value?.structuredOutput && criteriaTree.value.length > 0;
            // Start polling if:
            // 1. Status is processing OR we're in processing state
            // 2. We don't already have a poll interval running
            // 3. We don't have truly completed data
            const shouldPoll = (processing || status === "processing") && !pollInterval && !hasCompletedData;
            if (shouldPoll) {
                pollAttempts = 0; // Reset attempts when starting new polling
                pollInterval = setInterval(() => {
                    pollAttempts++;
                    // Stop polling if we've exceeded max attempts
                    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                        if (pollInterval) {
                            clearInterval(pollInterval);
                            pollInterval = null;
                        }
                        // Set status to failed with timeout message
                        // Safely check and update status
                        if (policyReview.value && typeof policyReview.value === "object") {
                            const currentStatus = policyReview.value.status;
                            if (currentStatus === "processing") {
                                policyReview.value.status = "failed";
                                policyReview.value.error =
                                    "Policy review is taking longer than expected (over 5 minutes). The review may still be processing. Please refresh the page or contact support if this persists.";
                            }
                        }
                        loading.value = false;
                        return;
                    }
                    // Always fetch to get the latest status from the API
                    // This ensures we detect completion as soon as it happens
                    fetchPolicyReview();
                }, POLL_INTERVAL_MS);
            }
            else if (!processing &&
                pollInterval &&
                (status === "completed" || status === "no_policy_found" || status === "failed" || status === "missing_input")) {
                clearInterval(pollInterval);
                pollInterval = null;
                pollAttempts = 0;
            }
        }, { immediate: true } // Run immediately to check initial state
        );
        // Provide helper functions to child components
        provide("getCriterionStatus", getCriterionStatus);
        provide("getPageAttachments", (id) => pageAttachments.value[id] || []);
        onMounted(() => {
            fetchPolicyReview();
        });
        onUnmounted(() => {
            // Clean up polling interval when component is unmounted
            if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
            pollAttempts = 0;
        });
        return (_ctx, _cache) => {
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_card_subtitle = _resolveComponent("v-card-subtitle");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_divider = _resolveComponent("v-divider");
            const _component_v_expansion_panel_title = _resolveComponent("v-expansion-panel-title");
            const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text");
            const _component_v_expansion_panel = _resolveComponent("v-expansion-panel");
            const _component_v_expansion_panels = _resolveComponent("v-expansion-panels");
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_card_actions = _resolveComponent("v-card-actions");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            const _component_v_snackbar = _resolveComponent("v-snackbar");
            return (shouldShow.value)
                ? (_openBlock(), _createBlock(_component_v_card, {
                    key: renderKey.value,
                    class: "mt-4 mb-4 policy-review-criteria-card",
                    color: "surface",
                    variant: "elevated",
                    elevation: "0"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                            default: _withCtx(() => [
                                _createElementVNode("h2", _hoisted_1, _toDisplayString(criteriaTitle.value), 1)
                            ]),
                            _: 1
                        }),
                        (hasCandidateSelector.value)
                            ? (_openBlock(), _createBlock(_component_v_card_subtitle, {
                                key: 0,
                                style: { "white-space": "normal" }
                            }, {
                                default: _withCtx(() => [
                                    (_openBlock(), _createBlock(_component_v_select, {
                                        key: selectedCandidateIndex.value,
                                        "model-value": selectedCandidateIndex.value,
                                        items: candidateItems.value,
                                        "item-value": "value",
                                        "item-title": "title",
                                        density: "compact",
                                        variant: "outlined",
                                        "hide-details": "",
                                        disabled: isBackendProcessing.value || switchingCandidate.value,
                                        loading: switchingCandidate.value,
                                        style: { "max-width": "500px" },
                                        "onUpdate:modelValue": onCandidateChange
                                    }, null, 8, ["model-value", "items", "disabled", "loading"])),
                                    (policyDocumentUrl.value && !isBackendProcessing.value && !switchingCandidate.value)
                                        ? (_openBlock(), _createElementBlock("a", {
                                            key: 0,
                                            href: policyDocumentUrl.value,
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                            class: "text-primary text-decoration-none policy-source-link mt-1 d-inline-flex align-center"
                                        }, [
                                            _createVNode(_component_v_icon, {
                                                icon: "mdi-open-in-new",
                                                size: "x-small",
                                                class: "mr-1"
                                            }),
                                            _cache[2] || (_cache[2] = _createTextVNode(" View original policy ", -1))
                                        ], 8, _hoisted_2))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }))
                            : (policySourceLabel.value && !isBackendProcessing.value)
                                ? (_openBlock(), _createBlock(_component_v_card_subtitle, {
                                    key: 1,
                                    style: { "white-space": "normal" }
                                }, {
                                    default: _withCtx(() => [
                                        (policyDocumentUrl.value)
                                            ? (_openBlock(), _createElementBlock("a", {
                                                key: 0,
                                                href: policyDocumentUrl.value,
                                                target: "_blank",
                                                rel: "noopener noreferrer",
                                                class: "text-primary text-decoration-none policy-source-link"
                                            }, [
                                                _createVNode(_component_v_icon, {
                                                    icon: "mdi-open-in-new",
                                                    size: "x-small",
                                                    class: "mr-1"
                                                }),
                                                _createTextVNode(" " + _toDisplayString(policySourceLabel.value), 1)
                                            ], 8, _hoisted_3))
                                            : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(policySourceLabel.value), 1))
                                    ]),
                                    _: 1
                                }))
                                : _createCommentVNode("", true),
                        (isProcessing.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 2 }, {
                                default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_5, [
                                        _createVNode(_component_v_progress_circular, {
                                            indeterminate: "",
                                            color: "primary",
                                            size: "24"
                                        }),
                                        _createElementVNode("span", _hoisted_6, [
                                            _cache[3] || (_cache[3] = _createTextVNode(" Policy review is running", -1)),
                                            (policyReview.value?.estimatedSeconds)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    _createTextVNode(", it usually takes " + _toDisplayString(policyReview.value.estimatedSeconds) + " seconds to execute", 1)
                                                ], 64))
                                                : _createCommentVNode("", true),
                                            _cache[4] || (_cache[4] = _createTextVNode("... ", -1))
                                        ])
                                    ])
                                ]),
                                _: 1
                            }))
                            : (hasError.value)
                                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 3 }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_alert, {
                                            type: errorAlertType.value,
                                            variant: "outlined",
                                            class: "mb-0"
                                        }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_7, _toDisplayString(errorTitle.value), 1),
                                                _createElementVNode("div", _hoisted_8, _toDisplayString(errorMessage.value), 1)
                                            ]),
                                            _: 1
                                        }, 8, ["type"]),
                                        (policyInputsChanged.value && !isBackendProcessing.value)
                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                key: 0,
                                                type: "info",
                                                variant: "tonal",
                                                density: "compact",
                                                class: "mt-2"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createElementVNode("div", _hoisted_9, [
                                                        _cache[6] || (_cache[6] = _createElementVNode("span", null, "A change to the policy fields was detected.", -1)),
                                                        _createVNode(_component_v_btn, {
                                                            size: "small",
                                                            color: "primary",
                                                            variant: "outlined",
                                                            loading: retryingPolicyReview.value,
                                                            class: "ml-4",
                                                            onClick: retryPolicyReview
                                                        }, {
                                                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                                                                _createTextVNode(" Re-run Policy Review ", -1)
                                                            ])),
                                                            _: 1,
                                                            __: [5]
                                                        }, 8, ["loading"])
                                                    ])
                                                ]),
                                                _: 1
                                            }))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                }))
                                : (criteriaTree.value.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_card_text, { key: 4 }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_10, [
                                                _createElementVNode("div", _hoisted_11, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: "mdi-check-circle",
                                                        color: "success",
                                                        size: "small"
                                                    }),
                                                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-medium-emphasis" }, "Present", -1))
                                                ]),
                                                _createElementVNode("div", _hoisted_12, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: "mdi-close-circle",
                                                        color: "error",
                                                        size: "small"
                                                    }),
                                                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-medium-emphasis" }, "Missing", -1))
                                                ]),
                                                _createVNode(_component_v_divider, {
                                                    vertical: "",
                                                    class: "h-4"
                                                }),
                                                _createElementVNode("div", _hoisted_13, [
                                                    _cache[9] || (_cache[9] = _createTextVNode(" Status: ", -1)),
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(overallStatus.value === 'Approved' ? 'text-success' : 'text-error')
                                                    }, _toDisplayString(overallStatus.value), 3)
                                                ])
                                            ]),
                                            _createElementVNode("div", _hoisted_14, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criteriaTree.value, (criterion) => {
                                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                                        key: criterion.id
                                                    }, [
                                                        (criterion.text === 'Medically Necessary Diagnoses' && criterion.children)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                _createVNode(CriteriaItem, {
                                                                    criterion: { ...criterion, children: [] },
                                                                    "current-page-id": _ctx.currentPageId,
                                                                    "page-manager": _ctx.pageManager,
                                                                    status: getCriterionStatus(criterion),
                                                                    "attached-pages": getAttachedPages(criterion.id),
                                                                    onStatusChange: onStatusChange,
                                                                    onPageAction: onPageAction
                                                                }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]),
                                                                _createElementVNode("div", _hoisted_15, [
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) === 'pass'), (child) => {
                                                                        return (_openBlock(), _createBlock(CriteriaItem, {
                                                                            key: child.id,
                                                                            criterion: child,
                                                                            "current-page-id": _ctx.currentPageId,
                                                                            "page-manager": _ctx.pageManager,
                                                                            status: getCriterionStatus(child),
                                                                            "attached-pages": getAttachedPages(child.id),
                                                                            onStatusChange: onStatusChange,
                                                                            onPageAction: onPageAction
                                                                        }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                    }), 128)),
                                                                    (criterion.children.filter((c) => getCriterionStatus(c) !== 'pass').length > 0)
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                                            _createVNode(_component_v_expansion_panels, {
                                                                                variant: "accordion",
                                                                                class: "medically-necessary-accordion"
                                                                            }, {
                                                                                default: _withCtx(() => [
                                                                                    _createVNode(_component_v_expansion_panel, null, {
                                                                                        default: _withCtx(() => [
                                                                                            _createVNode(_component_v_expansion_panel_title, { class: "text-body-2 text-center" }, {
                                                                                                default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                                                    _createTextVNode(" View full list of diagnoses ", -1)
                                                                                                ])),
                                                                                                _: 1,
                                                                                                __: [10]
                                                                                            }),
                                                                                            _createVNode(_component_v_expansion_panel_text, null, {
                                                                                                default: _withCtx(() => [
                                                                                                    _createElementVNode("div", _hoisted_17, [
                                                                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) !== 'pass'), (child) => {
                                                                                                            return (_openBlock(), _createBlock(CriteriaItem, {
                                                                                                                key: child.id,
                                                                                                                criterion: child,
                                                                                                                "current-page-id": _ctx.currentPageId,
                                                                                                                "page-manager": _ctx.pageManager,
                                                                                                                status: getCriterionStatus(child),
                                                                                                                "attached-pages": getAttachedPages(child.id),
                                                                                                                onStatusChange: onStatusChange,
                                                                                                                onPageAction: onPageAction
                                                                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                                                        }), 128))
                                                                                                    ])
                                                                                                ]),
                                                                                                _: 2
                                                                                            }, 1024)
                                                                                        ]),
                                                                                        _: 2
                                                                                    }, 1024)
                                                                                ]),
                                                                                _: 2
                                                                            }, 1024)
                                                                        ]))
                                                                        : _createCommentVNode("", true)
                                                                ])
                                                            ], 64))
                                                            : (_openBlock(), _createBlock(CriteriaItem, {
                                                                key: 1,
                                                                criterion: criterion,
                                                                "current-page-id": _ctx.currentPageId,
                                                                "page-manager": _ctx.pageManager,
                                                                status: getCriterionStatus(criterion),
                                                                "attached-pages": getAttachedPages(criterion.id),
                                                                onStatusChange: onStatusChange,
                                                                onPageAction: onPageAction
                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]))
                                                    ], 64));
                                                }), 128))
                                            ]),
                                            _createElementVNode("div", _hoisted_18, [
                                                (policyInputsChanged.value && !isBackendProcessing.value)
                                                    ? (_openBlock(), _createBlock(_component_v_alert, {
                                                        key: 0,
                                                        type: "info",
                                                        variant: "tonal",
                                                        density: "compact"
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createElementVNode("div", _hoisted_19, [
                                                                _cache[12] || (_cache[12] = _createElementVNode("span", null, "A change to the policy fields was detected.", -1)),
                                                                _createVNode(_component_v_btn, {
                                                                    size: "small",
                                                                    color: "primary",
                                                                    variant: "outlined",
                                                                    loading: retryingPolicyReview.value,
                                                                    class: "ml-4",
                                                                    onClick: retryPolicyReview
                                                                }, {
                                                                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                                                                        _createTextVNode(" Re-run Policy Review ", -1)
                                                                    ])),
                                                                    _: 1,
                                                                    __: [11]
                                                                }, 8, ["loading"])
                                                            ])
                                                        ]),
                                                        _: 1
                                                    }))
                                                    : _createCommentVNode("", true)
                                            ])
                                        ]),
                                        _: 1
                                    }))
                                    : (policyReview.value?.structuredOutput?.textInsideCriteriaDropdown && criteriaTree.value.length === 0)
                                        ? (_openBlock(), _createBlock(_component_v_card_text, { key: 5 }, {
                                            default: _withCtx(() => [
                                                _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-body-2 mb-2" }, [
                                                    _createElementVNode("strong", null, "Policy Criteria:")
                                                ], -1)),
                                                _createVNode(_component_v_expansion_panels, null, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_expansion_panel, null, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_v_expansion_panel_title, null, {
                                                                    default: _withCtx(() => [
                                                                        _createTextVNode(_toDisplayString(policyReview.value?.structuredOutput?.textHeaderCriteriaDropdown || "Policy Criteria"), 1)
                                                                    ]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_expansion_panel_text, null, {
                                                                    default: _withCtx(() => [
                                                                        _createElementVNode("div", _hoisted_20, _toDisplayString(policyReview.value?.structuredOutput?.textInsideCriteriaDropdown || ""), 1)
                                                                    ]),
                                                                    _: 1
                                                                })
                                                            ]),
                                                            _: 1
                                                        })
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1,
                                            __: [13]
                                        }))
                                        : (_openBlock(), _createBlock(_component_v_card_text, { key: 6 }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_21, [
                                                    (policyReview.value?.status === 'completed')
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, " Policy review completed, but no criteria data is available. "))
                                                        : (_openBlock(), _createElementBlock("div", _hoisted_23, "No approval criteria available."))
                                                ])
                                            ]),
                                            _: 1
                                        })),
                        _createVNode(_component_v_dialog, {
                            modelValue: showSwitchConfirmDialog.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((showSwitchConfirmDialog).value = $event)),
                            "max-width": "420"
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_card, null, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_card_title, { class: "text-h6" }, {
                                            default: _withCtx(() => _cache[14] || (_cache[14] = [
                                                _createTextVNode("Change Policy Document", -1)
                                            ])),
                                            _: 1,
                                            __: [14]
                                        }),
                                        _createVNode(_component_v_card_text, null, {
                                            default: _withCtx(() => _cache[15] || (_cache[15] = [
                                                _createTextVNode(" Changing the policy document will re-run criteria extraction against the patient's medical history. This may take up to 60 seconds. ", -1)
                                            ])),
                                            _: 1,
                                            __: [15]
                                        }),
                                        _createVNode(_component_v_card_actions, null, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_spacer),
                                                _createVNode(_component_v_btn, {
                                                    variant: "text",
                                                    onClick: cancelCandidateSwitch
                                                }, {
                                                    default: _withCtx(() => _cache[16] || (_cache[16] = [
                                                        _createTextVNode("Cancel", -1)
                                                    ])),
                                                    _: 1,
                                                    __: [16]
                                                }),
                                                _createVNode(_component_v_btn, {
                                                    color: "primary",
                                                    variant: "flat",
                                                    loading: switchingCandidate.value,
                                                    onClick: confirmCandidateSwitch
                                                }, {
                                                    default: _withCtx(() => _cache[17] || (_cache[17] = [
                                                        _createTextVNode(" Continue ", -1)
                                                    ])),
                                                    _: 1,
                                                    __: [17]
                                                }, 8, ["loading"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        }, 8, ["modelValue"]),
                        _createVNode(_component_v_snackbar, {
                            modelValue: errorSnackbar.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((errorSnackbar).value = $event)),
                            color: "error",
                            timeout: "5000",
                            location: "top"
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(errorSnackbarMessage.value), 1)
                            ]),
                            _: 1
                        }, 8, ["modelValue"])
                    ]),
                    _: 1
                }))
                : _createCommentVNode("", true);
        };
    }
});
